import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { eps } from '../../../services/url.service'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { CommonModule, NgFor, NgIf } from '@angular/common'
import { Business } from '../../../store/business/business.state'
import { loadBusinesses } from '../../../store/business/business.actions'
import { select, Store } from '@ngrx/store'
import { selectBusinesses } from '../../../store/business/business.reducer'
import {
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  startWith,
  Subject,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import {
  payrollBatchesActions,
  runningPayrollActions
} from '../../../store/payroll/payroll.actions'
import { selectPayrollBatches, selectRunningPayroll } from '../../../store/payroll/payroll.reducers'
import {
  IncomingPayrollBatch,
  IncomingRunningPayrollBatch,
  PayrollStatus
} from '../../../store/payroll/payroll.state'
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { selectUser } from '../../../store/auth/auth.state'
import * as Papa from 'papaparse'
import { ToastrService } from 'ngx-toastr'

interface PayrollByQuarter {
  Q1: IncomingPayrollBatch[]
  Q2: IncomingPayrollBatch[]
  Q3: IncomingPayrollBatch[]
  Q4: IncomingPayrollBatch[]
}

enum PaymentMode {
  CASH = 'cash',
  CHEQUE = 'cheque',
  NEFT = 'neft',
  RTGS = 'rtgs',
  IMPS = 'imps',
  UPI = 'upi'
}

@Component({
  selector: 'app-manage-payroll',
  imports: [NgIf, NgFor, RouterModule, CommonModule, ReactiveFormsModule, CapitalizePipe],
  templateUrl: './manage-payroll.component.html',
  styleUrl: './manage-payroll.component.scss'
})
export class ManagePayrollComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private modalService = inject(NgbModal)
  private toastr = inject(ToastrService)
  private router = inject(Router)
  private fb = inject(FormBuilder)
  private destroy$ = new Subject<void>()
  private readonly FETCH_DELAY_MS = 1500
  eps = eps
  @ViewChild('finalizePayrollModal', { static: true })
  finalizePayrollModal!: TemplateRef<NgbActiveModal>
  PayrollStatus = PayrollStatus
  financialYears: string[] = []
  selectedFinancialYear = new FormControl('', { nonNullable: true })
  selectedBusiness = new FormControl('', { nonNullable: true })
  businessesList: Business[] = []
  payrollPaymentDetailsForm!: FormGroup
  selectedPayrollBatchId = '' // For Delete Modal

  visibleQuarters = new Set<string>()
  payrollDataByQuarter: PayrollByQuarter = { Q1: [], Q2: [], Q3: [], Q4: [] }

  isExporting = false
  exportingBatchId: string | null = null
  currentQuarter!: 'Q1' | 'Q2' | 'Q3' | 'Q4'
  month = 4
  ngOnInit(): void {
    this.setupFinancialYears()
    this.loadBusinessesList()
    this.listenForSelectionChanges()
    this.subscribeToPayrollBatches()
    this.initializePaymentDetailsForm()
    this.month = new Date().getMonth() + 1 // 1-12
    if (this.month >= 4 && this.month <= 6) this.currentQuarter = 'Q1'
    else if (this.month >= 7 && this.month <= 9) this.currentQuarter = 'Q2'
    else if (this.month >= 10 && this.month <= 12) this.currentQuarter = 'Q3'
    else this.currentQuarter = 'Q4' // Jan - Mar
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initializePaymentDetailsForm(): void {
    this.payrollPaymentDetailsForm = this.fb.group({
      dateOfPayment: [new Date().toISOString().slice(0, 10), Validators.required],
      modeOfPayment: [PaymentMode.NEFT, Validators.required]
    })
  }

  //kripesh
  // private setupFinancialYears(): void {
  //   const currentYear = new Date().getFullYear()
  //   const START_YEAR = 2023 // Adjust as needed

  //   for (let year = START_YEAR; year <= currentYear + 1; year++) {
  //     this.financialYears.unshift(`${year - 1}-${year}`)
  //   }
  //   if (this.financialYears.length > 0) {
  //     this.selectedFinancialYear.setValue(this.financialYears[0])
  //   }
  // }

  //gps 7 january 2026
  private setupFinancialYears(): void {
    const today = new Date()
    const currentYear = today.getFullYear()
    const START_YEAR = 2023 // Adjust as needed

    // Determine the current financial year
    let fyCurrentYear: number
    if (today.getMonth() + 1 >= 4) {
      // April or later → FY = currentYear-currentYear+1
      fyCurrentYear = currentYear
    } else {
      // Jan-Mar → FY = previousYear-currentYear
      fyCurrentYear = currentYear - 1
    }

    this.financialYears = []

    for (let year = START_YEAR; year <= fyCurrentYear; year++) {
      this.financialYears.unshift(`${year}-${year + 1}`)
    }

    if (this.financialYears.length > 0) {
      this.selectedFinancialYear.setValue(this.financialYears[0])
    }
  }

  private loadBusinessesList(): void {
    this.store.dispatch(loadBusinesses({ params: `module=payroll` }))
    this.store
      .pipe(
        select(selectBusinesses),
        filter(businesses => !!businesses && businesses.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(businesses => {
        this.businessesList = businesses
        if (businesses.length === 1) {
          this.selectedBusiness.setValue(businesses[0].id)
        }
      })
  }

  private listenForSelectionChanges(): void {
    combineLatest([
      this.selectedFinancialYear.valueChanges.pipe(startWith(this.selectedFinancialYear.value)),
      this.selectedBusiness.valueChanges.pipe(startWith(this.selectedBusiness.value))
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([year, businessId]) => !!year && !!businessId),
        distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]),
        delay(this.FETCH_DELAY_MS),
        tap(([yearRange, businessId]) => {
          this.clearPayrollDataByQuarter()
          const [startYear, endYear] = yearRange.split('-').map(Number)
          const startDate = `${startYear}-04-01` // Financial year starts April 1
          const endDate = `${endYear}-03-31` // Ends March 31 next year
          // Recalculate visible quarters relative to selected year
          const today = new Date()
          if (today >= new Date(startDate) && today <= new Date(endDate)) {
            this.setVisibleQuarters(today)
          } else {
            this.visibleQuarters = new Set(['Q1', 'Q2', 'Q3', 'Q4'])
          }
          this.store.dispatch(
            payrollBatchesActions.loadPayrollBatches({
              financialYear: yearRange,
              belongsTo: businessId
            })
          )
        })
      )
      .subscribe()
  }

  private subscribeToPayrollBatches(): void {
    this.store.pipe(select(selectPayrollBatches), takeUntil(this.destroy$)).subscribe(batches => {
      // Repopulate data when batches arrive AND selections are valid
      if (this.selectedFinancialYear.value && this.selectedBusiness.value) {
        this.populatePayrollDataByQuarter(batches || [])
        // ✅ Now call setVisibleQuarters
        this.setVisibleQuarters(new Date())
      } else {
        this.clearPayrollDataByQuarter() // Clear if selections become invalid after fetch
      }
    })
  }

  private clearPayrollDataByQuarter(): void {
    this.payrollDataByQuarter = { Q1: [], Q2: [], Q3: [], Q4: [] }
  }

  private populatePayrollDataByQuarter(fetchedBatches: IncomingPayrollBatch[]): void {
    // 1) Reset
    this.clearPayrollDataByQuarter()

    // 2) Parse selected FY, e.g. "2024-2025"
    const fy = this.selectedFinancialYear.value
    if (!fy) {
      return
    }
    const [fyStartStr, fyEndStr] = fy.split('-')
    const fyStart = parseInt(fyStartStr, 10)
    const fyEnd = parseInt(fyEndStr, 10)

    // 3) Define quarters → month numbers
    const quarters: Record<keyof PayrollByQuarter, number[]> = {
      Q1: [4, 5, 6], // Apr, May, Jun
      Q2: [7, 8, 9], // Jul, Aug, Sep
      Q3: [10, 11, 12], // Oct, Nov, Dec
      Q4: [1, 2, 3] // Jan, Feb, Mar
    }

    const today = new Date()

    for (const [qKey, months] of Object.entries(quarters) as [keyof PayrollByQuarter, number[]][]) {
      for (const month of months) {
        // decide calendar‐year: Apr–Dec use fyStart; Jan–Mar use fyEnd
        const year = month >= 4 ? fyStart : fyEnd

        // skip future months
        const firstOfMonth = new Date(year, month - 1, 1)
        if (firstOfMonth > today) {
          continue
        }
        // look for an existing batch whose payPeriodStart falls in this month
        const existing = fetchedBatches.find(b => {
          const d = new Date(b.payPeriodStart)
          // console.log(
          //   'full year: ',
          //   d.getFullYear(),
          //   'year: ',
          //   year,
          //   'get month: ',
          //   d.getMonth() + 1,
          //   'month: ',
          //   month,
          //   'result: ',
          //   d.getFullYear() === year && d.getMonth() + 1 === month
          // )
          return d.getFullYear() === year && d.getMonth() + 1 === month
        })

        if (existing) {
          // console.log('existing', existing)
          this.payrollDataByQuarter[qKey].push(existing)
        } else {
          // compute month start/end strings
          const mm = String(month).padStart(2, '0')
          const start = `${year}-${mm}-01`
          // day 0 of next month gives last day of this month
          const endDay = new Date(year, month, 0).getDate()
          const end = `${year}-${mm}-${String(endDay).padStart(2, '0')}`
          // console.log('start: ', start, ' end: ', end, 'month: ', month)
          const placeholder: IncomingPayrollBatch = {
            id: '',
            belongsTo: this.selectedBusiness.value,
            payPeriodStart: start,
            payPeriodEnd: end,
            netAmount: 0,
            investmentsLockedAt: null,
            totalTdsAfterCess: 0,
            totalAmount: 0,
            employeeCount: 0,
            status: PayrollStatus.PENDING,
            paidBy: null,
            paidByUser: null,
            dateOfPayment: null,
            processedBy: null,
            processedByUser: null,
            processedOn: null,
            updatedAt: ''
          }

          this.payrollDataByQuarter[qKey].push(placeholder)
        }
      }
    }
  }

  // private setVisibleQuarters(today: Date) {
  //   const month = today.getMonth() // 0 = Jan, 11 = Dec
  //   this.visibleQuarters.clear()

  //   if (month >= 3) this.visibleQuarters.add('Q1') // Apr-Jun
  //   if (month >= 6) this.visibleQuarters.add('Q2') // Jul-Sep
  //   if (month >= 9) this.visibleQuarters.add('Q3') // Oct-Dec
  //   if (month >= 0 && month <= 2) this.visibleQuarters.add('Q4') // Jan-Mar (of next year)
  //   console.log('payroll data by quarter', this.payrollDataByQuarter)
  // }

  // private setVisibleQuarters(today: Date) {
  //   const month = today.getMonth() + 1 // 1 = Jan, 12 = Dec
  //   const year = today.getFullYear()

  //   // Always show all quarters
  //   this.visibleQuarters.clear()
  //   // this.visibleQuarters.add('Q1')
  //   // this.visibleQuarters.add('Q2')
  //   // this.visibleQuarters.add('Q3')
  //   // this.visibleQuarters.add('Q4')
  //   if (month >= 4) this.visibleQuarters.add('Q1') // Apr-Jun
  //   if (month >= 7) this.visibleQuarters.add('Q2') // Jul-Sep
  //   if (month >= 10) this.visibleQuarters.add('Q3') // Oct-Dec
  //   if (month >= 1 && month <= 3) {
  //     this.visibleQuarters.add('Q1')
  //     this.visibleQuarters.add('Q2')
  //     this.visibleQuarters.add('Q3')
  //     this.visibleQuarters.add('Q4') // Jan-Mar (of next year)
  //   }

  //   // Figure out last month
  //   const lastMonth = month === 1 ? 12 : month - 1
  //   const lastMonthYear = month === 1 ? year - 1 : year

  //   // Check if payroll for last month exists (real one, not placeholder)
  //   let lastMonthExists = false
  //   // this.subscribeToPayrollBatches()
  //   // for (const [qKey, batches] of Object.entries(this.payrollDataByQuarter) as [
  //   //   keyof PayrollByQuarter,
  //   //   IncomingPayrollBatch[]
  //   // ][]) {
  //   //   for (const b of batches) {
  //   //     if (!b.payPeriodStart) continue
  //   //     const d = new Date(b.payPeriodStart)
  //   //     console.log('batches', b)
  //   //     console.log('d', d.getFullYear())
  //   //     if (d.getFullYear() === lastMonthYear && d.getMonth() + 1 === lastMonth) {
  //   //       console.log('batch', b)
  //   //       if (b.id) {
  //   //         // only treat as exists if it has a DB id
  //   //         lastMonthExists = true
  //   //         break
  //   //       }
  //   //     }
  //   //     if (lastMonthExists) break
  //   //   }
  //   // }
  //   for (const batches of Object.values(this.payrollDataByQuarter)) {
  //     for (const b of batches) {
  //       if (!b.payPeriodStart) continue
  //       const d = new Date(b.payPeriodStart)
  //       if (d.getFullYear() === lastMonthYear && d.getMonth() + 1 === lastMonth && b.id) {
  //         lastMonthExists = true
  //         break
  //       }
  //     }
  //     if (lastMonthExists) break
  //   }
  //   // Decide which quarter is current
  //   // if (!lastMonthExists) {
  //   //   // Stay in last quarter
  //   //   if (lastMonth >= 4 && lastMonth <= 6) this.currentQuarter = 'Q1'
  //   //   else if (lastMonth >= 7 && lastMonth <= 9) this.currentQuarter = 'Q2'
  //   //   else if (lastMonth >= 10 && lastMonth <= 12) this.currentQuarter = 'Q3'
  //   //   else if (month >= 1 && month <= 3) this.currentQuarter = 'Q4'
  //   // } else {
  //   //   console.log('Current quarter:', this.currentQuarter)
  //   //   // Normal → use today's month
  //   //   if (month >= 4 && month <= 6) this.currentQuarter = 'Q1'
  //   //   else if (month >= 7 && month <= 9) this.currentQuarter = 'Q2'
  //   //   else if (month >= 10 && month <= 12) this.currentQuarter = 'Q3'
  //   //   else if (month >= 1 && month <= 3) this.currentQuarter = 'Q4'
  //   // }

  //   //gps 8 january 2026
  //   // Special case: December exists → Q1
  //   if (lastMonthExists && lastMonth === 12) {
  //     this.currentQuarter = 'Q4'
  //   } else if (!lastMonthExists) {
  //     // Stay in last quarter
  //     if (lastMonth >= 4 && lastMonth <= 6) this.currentQuarter = 'Q1'
  //     else if (lastMonth >= 7 && lastMonth <= 9) this.currentQuarter = 'Q2'
  //     else if (lastMonth >= 10 && lastMonth <= 12) this.currentQuarter = 'Q3'
  //     else if (month >= 1 && month <= 3) this.currentQuarter = 'Q4'
  //   } else {
  //     // if (this.selectedFinancialYear) {
  //     //   const startYear = parseInt(this.selectedFinancialYear.split('-')[0])
  //     //   const endYear = startYear + 1

  //     //   this.month = new Date(endYear, 2, 1).getMonth() + 1 // March = 3
  //     // }

  //     const fy = this.selectedFinancialYear.value // "2025-26"
  //     if (fy) {
  //       const startYear = parseInt(fy.split('-')[0])
  //       const endYear = startYear + 1
  //       this.month = new Date(endYear, 2, 1).getMonth() + 1 // March = 3
  //       console.log('month', this.month) // 2025 2026
  //     }
  //     // Normal → use today's month
  //     if (month >= 4 && month <= 6) this.currentQuarter = 'Q1'
  //     else if (month >= 7 && month <= 9) this.currentQuarter = 'Q2'
  //     else if (month >= 10 && month <= 12) this.currentQuarter = 'Q3'
  //     else if (month >= 1 && month <= 3) this.currentQuarter = 'Q4'
  //   }
  // }

  //gps 1 april 2026
  // private setVisibleQuarters(today: Date) {
  //   const month = today.getMonth() + 1 // 1 = Jan
  //   const year = today.getFullYear()

  //   this.visibleQuarters.clear()

  //   const fy = this.selectedFinancialYear.value // e.g. "2025-26"
  //   let selectedStartYear: number | undefined
  //   let selectedEndYear: number | undefined

  //   if (fy) {
  //     selectedStartYear = parseInt(fy.split('-')[0])
  //     selectedEndYear = selectedStartYear + 1
  //   }

  //   // ---- Determine current FY ----
  //   let currentFYStartYear: number
  //   let currentFYEndYear: number
  //   if (month >= 4) {
  //     currentFYStartYear = year
  //     currentFYEndYear = year + 1
  //   } else {
  //     currentFYStartYear = year - 1
  //     currentFYEndYear = year
  //   }

  //   // ---- Show all quarters if selected FY is before current FY ----
  //   if (selectedStartYear && selectedStartYear < currentFYStartYear) {
  //     ;['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => this.visibleQuarters.add(q))
  //     this.currentQuarter = 'Q1'
  //     this.month = new Date(selectedEndYear!, 2, 1).getMonth() + 1 // March
  //     return
  //   }

  //   // ---- Otherwise: check payroll batches for each quarter (latest first) ----
  //   const quarterOrder: ('Q4' | 'Q3' | 'Q2' | 'Q1')[] = ['Q4', 'Q3', 'Q2', 'Q1']
  //   for (const q of quarterOrder) {
  //     const batches = this.payrollDataByQuarter[q] || []
  //     if (batches.some(b => b.id)) {
  //       this.currentQuarter = q
  //       break
  //     }
  //   }

  //   // ---- Fill visible quarters based on month ----
  //   if (month >= 4) this.visibleQuarters.add('Q1')
  //   if (month >= 7) this.visibleQuarters.add('Q2')
  //   if (month >= 10) this.visibleQuarters.add('Q3')
  //   if (month >= 1 && month <= 3) this.visibleQuarters.add('Q4')

  //   // ---- Optional: month = March of selected FY ----
  //   if (selectedStartYear && selectedEndYear) {
  //     this.month = new Date(selectedEndYear, 2, 1).getMonth() + 1 // March
  //   }
  // }

  private setVisibleQuarters(today: Date) {
    const fy = this.selectedFinancialYear.value // e.g. "2025-26"
    if (!fy) return

    const startYear = parseInt(fy.split('-')[0])
    const endYear = startYear + 1

    const todayMonth = today.getMonth() + 1 // 1 = Jan
    const todayYear = today.getFullYear()

    // Determine current FY
    let currentFYStartYear: number
    if (todayMonth >= 4) currentFYStartYear = todayYear
    else currentFYStartYear = todayYear - 1

    // ---- Determine the current quarter of today ----
    let currentQuarterOfFY: 'Q1' | 'Q2' | 'Q3' | 'Q4'
    if (todayMonth >= 4 && todayMonth <= 6) currentQuarterOfFY = 'Q1'
    else if (todayMonth >= 7 && todayMonth <= 9) currentQuarterOfFY = 'Q2'
    else if (todayMonth >= 10 && todayMonth <= 12) currentQuarterOfFY = 'Q3'
    else currentQuarterOfFY = 'Q4' // Jan-Mar

    // ---- Prepare visible quarters ----
    this.visibleQuarters.clear()
    const allQuarters: ('Q1' | 'Q2' | 'Q3' | 'Q4')[] = ['Q1', 'Q2', 'Q3', 'Q4']

    if (startYear < currentFYStartYear) {
      // Past FY → show all quarters
      allQuarters.forEach(q => this.visibleQuarters.add(q))
    } else {
      // Current or future FY → show only quarters up to current quarter
      for (const q of allQuarters) {
        this.visibleQuarters.add(q)
        if (q === currentQuarterOfFY) break
      }
    }

    // ---- Determine active quarter based on payroll, starting from Q4 → Q1 ----
    let activeQuarter: 'Q1' | 'Q2' | 'Q3' | 'Q4' = 'Q1'
    const quarterOrder: ('Q4' | 'Q3' | 'Q2' | 'Q1')[] = ['Q4', 'Q3', 'Q2', 'Q1']

    for (const q of quarterOrder) {
      if (!this.visibleQuarters.has(q)) continue // skip future quarters
      const batches = this.payrollDataByQuarter[q] || []
      const hasPayrollInFY = batches.some(b => {
        if (!b.payPeriodStart || !b.id) return false
        const d = new Date(b.payPeriodStart)
        const batchFYStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1
        return batchFYStart === startYear
      })
      if (hasPayrollInFY) {
        activeQuarter = q
        break
      }
    }

    this.currentQuarter = activeQuarter

    // Optional: set month to March of FY
    this.month = new Date(endYear, 2, 1).getMonth() + 1 // March
  }
  setupRunningPayroll(batchData: IncomingPayrollBatch, finalize = false) {
    if (batchData.status === PayrollStatus.PROCESSED && finalize) {
      this.openFinalizePayrollModal(this.finalizePayrollModal, batchData.id)
    } else {
      this.store.dispatch(
        runningPayrollActions.setupRunningPayroll({
          batchData: { ...batchData, payrolls: [], processedBy: null, paidBy: null }
        })
      )
      this.router.navigate([eps.hrm.runPayrollPath])
    }
  }

  private openFinalizePayrollModal(modal: unknown, batchId: string) {
    this.selectedPayrollBatchId = batchId
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'md'
    })
  }

  finalizePayroll(batchId: string) {
    const data = this.payrollPaymentDetailsForm.value
    data.batchId = batchId
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        if (!user) return
        data.paidBy = user.id
      })
    this.store.dispatch(payrollBatchesActions.finalizePayroll(data))
    this.onClose()
  }

  openDeleteModal(modal: unknown, batchId: string) {
    this.selectedPayrollBatchId = batchId
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'sm'
    })
  }

  deletePayrollBatch(batchId: string) {
    this.store.dispatch(payrollBatchesActions.deletePayrollBatch({ id: batchId }))
    this.onClose()
  }

  onClose() {
    this.selectedPayrollBatchId = ''
    this.modalService.dismissAll()
  }

  async exportPayrollToCsv(batchData: IncomingPayrollBatch): Promise<void> {
    // 1. Guard against trying to export a placeholder batch
    if (!batchData.id) {
      this.toastr.error("This month's payroll has not been generated yet.")
      return
    }

    // 2. Set loading state for better UX
    this.isExporting = true
    this.exportingBatchId = batchData.id

    try {
      // 3. Dispatch action to fetch detailed payroll data including employees
      this.store.dispatch(
        runningPayrollActions.loadRunningPayroll({
          payPeriodStart: batchData.payPeriodStart,
          payPeriodEnd: batchData.payPeriodEnd,
          belongsTo: batchData.belongsTo
        })
      )

      // 4. Wait for the detailed data to be loaded into the store
      const fullPayrollBatch = await firstValueFrom(
        this.store.pipe(
          select(selectRunningPayroll),
          // Filter to ensure we have the data and it's for the batch we requested
          filter(data => !!data && data.id === batchData.id && data.payrolls.length > 0)
        )
      )

      if (!fullPayrollBatch) {
        return
      }

      // 5. Prepare the data for CSV conversion
      const csvData = fullPayrollBatch.payrolls.map(payroll => ({
        'Financial Year': this.selectedFinancialYear.value,
        Month: new Date(fullPayrollBatch.payPeriodStart).toLocaleString('default', {
          month: 'long'
        }),
        'Employee Name': `${payroll.employee.firstName} ${payroll.employee.lastName}`,
        'Employee ID': payroll.employee.empId,
        'Gross Salary': payroll.grossSalary,
        'Net Salary': payroll.netSalary,
        'TDS (after cess)': payroll.tdsAfterCess,
        'LOP Days': payroll.lopDays,
        Bonus: payroll.bonus,
        Expenses: payroll.expenses,
        Adjustments: payroll.adjustments,
        Rounding: payroll.rounding
      }))

      // 6. Use PapaParse to convert JSON to a CSV string
      const csvString = Papa.unparse(csvData)

      // 7. Trigger the browser download
      this.downloadCsv(csvString, fullPayrollBatch)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      this.toastr.error('Failed to export payroll data. Please try again.')
    } finally {
      // 8. Reset loading state
      this.isExporting = false
      this.exportingBatchId = null
    }
  }

  /**
   * A helper utility to trigger the download of the generated CSV string.
   * @param csvString The CSV content as a string.
   * @param batch The payroll batch used for generating a filename.
   */
  private downloadCsv(csvString: string, batch: IncomingRunningPayrollBatch): void {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })

    // Generate a descriptive filename
    const date = new Date(batch.payPeriodStart)
    const monthName = date.toLocaleString('default', { month: 'long' })
    const year = date.getFullYear()
    const fileName = `Payroll-${monthName}-${year}.csv`

    // Create a temporary link to trigger the download
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
